import { useId } from "react";
import clsx from "clsx";
import type { Dayjs } from "@marekl/adminer/datetime";
import type { MessageState } from "../../lib/queries/messages";
import styles from "./Postmark.module.css";

/**
 * The cancellation mark on the message detail: a ring naming the instance and
 * the sender, the date and time it was struck, and the state as the killer
 * word — the one flourish in the theme.
 *
 * The date is `sent_at` for a message that went out and `created_at` for one
 * that did not — the API carries no state-change timestamp, so a failed or
 * expired message is stamped with the day it was enqueued. The delivery
 * timeline below it is where the exact moment of each transition lives.
 */

/** The word struck across the middle. The app's own state labels, in caps. */
const POSTMARK_WORD: Record<MessageState, string> = {
  pending: "QUEUED",
  leased: "SENDING",
  sent: "SENT",
  failed: "FAILED",
  cancelled: "CANCELLED",
  suppressed: "SUPPRESSED",
  expired: "EXPIRED",
};

/** Ink per state. Franking blue when it went out, postmark red when it did not. */
const POSTMARK_INK: Record<MessageState, string> = {
  pending: "var(--adminer-color-info)",
  leased: "var(--adminer-color-info)",
  sent: "var(--adminer-color-primary)",
  failed: "var(--adminer-color-err)",
  cancelled: "var(--adminer-color-ink-dim)",
  suppressed: "var(--adminer-color-warn)",
  expired: "var(--adminer-color-warn)",
};

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

/** `17 IX 2026` — the roman month a hand-struck postmark carries. */
function postmarkDate(at: Dayjs): string {
  return `${at.date()} ${ROMAN[at.month()]} ${at.year()}`;
}

/** Wavy cancellation lines, drawn to the right of the ring. */
function waves(x: number, ys: number[], segments: number, amplitude: number): string {
  return ys
    .map((y) => {
      let d = `M${x} ${y}`;
      for (let s = 0; s < segments; s++) {
        d += s === 0 ? ` q 6.25 -${amplitude} 12.5 0` : " t 12.5 0";
      }
      return d;
    })
    .join(" ");
}

export interface PostmarkProps {
  state: MessageState;
  /** Sender key, set around the ring. */
  senderKey: string;
  /** When the state was reached. */
  at: Dayjs;
  className?: string;
}

export function Postmark({ state, senderKey, at, className }: PostmarkProps) {
  // Ids must be unique per instance: two postmarks on one page would otherwise
  // share a filter and a text path.
  const uid = useId().replace(/:/g, "");
  const inkId = `pm-ink-${uid}`;
  const ringId = `pm-ring-${uid}`;
  const word = POSTMARK_WORD[state];
  const date = postmarkDate(at);
  const time = at.format("HH:mm");
  const ring = `LETTERMAN • ${senderKey.toUpperCase()} • ${word} • `;

  return (
    <svg
      viewBox="0 0 230 140"
      className={clsx(styles.postmark, className)}
      style={{ color: POSTMARK_INK[state] }}
      role="img"
      aria-label={`Postmark: ${word}, ${date} ${time}, ${senderKey}`}
    >
      <defs>
        {/*
          Rubber never prints solid. Fractal noise multiplied into the alpha
          channel breaks the ink up; `forced-colors` drops the filter so the
          mark stays a clean outline in high-contrast mode (see the module CSS).
        */}
        <filter id={inkId} x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" seed="7" result="noise" />
          <feColorMatrix
            in="noise"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -1.7 1.55"
            result="ink"
          />
          <feComposite in="SourceGraphic" in2="ink" operator="in" />
        </filter>
        <path id={ringId} d="M 70 70 m -46 0 a 46 46 0 1 1 92 0 a 46 46 0 1 1 -92 0" />
      </defs>
      <g className={styles.ink} filter={`url(#${inkId})`} fill="none" stroke="currentColor">
        <circle cx="70" cy="70" r="57" strokeWidth="2.6" />
        <circle cx="70" cy="70" r="35" strokeWidth="1.3" />
        <g strokeWidth="2.4">
          <path d={waves(132, [44, 57, 70, 83, 96], 7, 5)} />
        </g>
        <path d="M38 66 H102 M38 86 H102" strokeWidth="1.3" />
        <g fill="currentColor" stroke="none">
          <text className={styles.ringText}>
            <textPath href={`#${ringId}`} textLength="286" lengthAdjust="spacing">
              {ring}
            </textPath>
          </text>
          <text x="70" y="61" textAnchor="middle" className={styles.date}>
            {date}
          </text>
          <text
            x="70"
            y="81"
            textAnchor="middle"
            className={styles.word}
            style={{ fontSize: word.length > 7 ? 11.5 : 15 }}
          >
            {word}
          </text>
          <text x="70" y="99" textAnchor="middle" className={styles.time}>
            {time}
          </text>
        </g>
      </g>
    </svg>
  );
}
